import { motion } from 'framer-motion';
import { CheckCircle, Zap, Users, LogIn } from 'lucide-react';
import {Link } from "react-router-dom";

const CTA = () => {
  const benefits = [
    "No upfront costs or fees",
    "Instant access to 500+ deals",
    "Weekly payments",
    "24/7 support",
    "Mobile-friendly dashboard",
    "Real-time analytics"
  ];

  return (
    <section id="contact" className="overflow-hidden relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-[#FF7F7F]/10 to-pink-500/10"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#FF7F7F] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-500 rounded-full opacity-10 mix-blend-multiply filter blur-3xl animate-pulse animation-delay-2000"></div>
      </div>

      <div className="relative z-10 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 items-center lg:grid-cols-2">
          {/* Left side - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="mb-6 text-4xl font-bold leading-tight text-white md:text-5xl">
              Ready to Start
              <span className="bg-gradient-to-r from-[#FF7F7F] to-pink-500 bg-clip-text text-transparent"> Earning?</span>
            </h2>
            <p className="mb-8 text-xl leading-relaxed text-gray-300">
              Access your affiliate dashboard and start promoting deals immediately. 
              Track your earnings, manage your campaigns, and grow your income today!
            </p>

            {/* Benefits */}
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex gap-3 items-center"
                >
                  <CheckCircle className="w-5 h-5 text-[#FF7F7F] flex-shrink-0" />
                  <span className="text-gray-300">{benefit}</span>
                </motion.div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#FF7F7F] mb-2">10K+</div>
                <div className="text-sm text-gray-400">Active Promoters</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#FF7F7F] mb-2">4.9/5</div>
                <div className="text-sm text-gray-400">Platform Rating</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#FF7F7F] mb-2">500+</div>
                <div className="text-sm text-gray-400">Premium Deals</div>
              </div>
            </div>

            <Link to="/login">
              <button className="bg-gradient-to-r from-[#FF7F7F] to-pink-500 hover:from-[#ff6b6b] hover:to-pink-400 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2">
                Access Dashboard
                <LogIn className="w-5 h-5" />
              </button>
            </Link>
          </motion.div>

          {/* Right side - Login CTA */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="p-8 rounded-3xl border backdrop-blur-sm bg-white/10 border-white/20">
              <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-[#FF7F7F] to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LogIn className="w-8 h-8 text-white" />
                </div>
                <h3 className="mb-2 text-2xl font-bold text-white">Welcome Back!</h3>
                <p className="text-gray-300">Sign in to your affiliate dashboard and continue earning</p>
              </div>

              <div className="space-y-6">
                <div className="text-center">
                  <p className="mb-4 text-gray-300">
                    Already have an account? Sign in with your email or phone number to access your dashboard.
                  </p>
                  <Link to="/login">
                    <button className="w-full bg-gradient-to-r from-[#FF7F7F] to-pink-500 hover:from-[#ff6b6b] hover:to-pink-400 text-white py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2">
                      <LogIn className="w-5 h-5" />
                      Sign In to Dashboard
                    </button>
                  </Link>
                </div>

                <div className="text-center">
                  <p className="mb-2 text-sm text-gray-400">Need help accessing your account?</p>
                  <Link to="/forgot-password" className="text-[#FF7F7F] hover:underline text-sm">
                    Reset Password
                  </Link>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-400">
                  By signing in, you agree to our{' '}
                  <Link to="/terms" className="text-[#FF7F7F] hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-[#FF7F7F] hover:underline">Privacy Policy</Link>
                </p>
              </div>
            </div>

            {/* Floating elements */}
            <div className="absolute -top-4 -right-4 bg-[#FF7F7F] rounded-full p-3 animate-bounce">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="absolute -bottom-4 -left-4 p-3 bg-purple-500 rounded-full animate-pulse">
              <Users className="w-6 h-6 text-white" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
